import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { AddJobDialog } from "./AddJobDialog";
import { SharedKanbanBoard } from "@/components/jobs/kanban/SharedKanbanBoard";
import { useKanbanMatches } from "@/hooks/matches/useKanbanMatches";
import { useCandidateKanban } from "@/hooks/candidates/useCandidateKanban";
import { useTranslation } from "react-i18next";
import { CandidateHiredWorkforceModal } from "./CandidateHiredWorkforceModal";
import { KanbanCandidate } from "@/components/jobs/kanban/types";

interface CandidateKanbanProps {
	candidateId: string;
	candidateName?: string;
	refreshKey?: number;
}

export const CandidateKanban = ({ candidateId, candidateName, refreshKey }: CandidateKanbanProps) => {
	const { t } = useTranslation();
	const { loading, matches, handleStageChange, refreshMatches } = useKanbanMatches({ candidateId, showAllMatches: true });
	const [hiredMatch, setHiredMatch] = useState<KanbanCandidate | null>(null);

	useEffect(() => {
		if (refreshKey && refreshKey > 0)
			refreshMatches();
	}, [refreshKey]);
	const {
		isAddJobOpen,
		setIsAddJobOpen,
		handleAddJob,
		addMultipleJobs,
	} = useCandidateKanban(candidateId);

	if (loading) {
		return (
			<div className="flex justify-center p-8">
				<div className="animate-spin h-8 w-8 border-2 border-digger-blue border-t-transparent rounded-full"></div>
			</div>
		);
	}

	const handleAddJobsConfirmWithRefresh = async (jobIds: string[]) => {
		await addMultipleJobs(jobIds);
		// Refresh the matches after adding jobs
		refreshMatches();
	};

	return (
		<div className="w-full">
			<div className="flex justify-end mb-4 gap-2">
				<Button
					onClick={handleAddJob}
					className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90 flex items-center gap-1"
				>
					<Plus className="h-4 w-4" />
					<span>Add Job</span>
				</Button>
			</div>

			<SharedKanbanBoard
				candidateId={candidateId}
				matches={matches}
				onStageChange={handleStageChange}
				onRefresh={refreshMatches}
				onMatchHired={(match) => setHiredMatch(match)}
				showAllMatches
			/>

			<AddJobDialog
				isOpen={isAddJobOpen}
				onClose={() => setIsAddJobOpen(false)}
				onAddJobs={handleAddJobsConfirmWithRefresh}
			/>

			{hiredMatch && (
				<CandidateHiredWorkforceModal
					isOpen={true}
					onClose={() => setHiredMatch(null)}
					candidateId={candidateId}
					candidateName={candidateName || ""}
				/>
			)}
		</div>
	);
};
